// ── Adapter do IPasswordResetRepository ───────────────────────────
// Única camada que conhece as RPCs `*_password_reset`. Elas rodam como
// SECURITY DEFINER: leem `auth.users`, trocam a senha e disparam o
// e-mail pelo n8n de dentro do banco — o token nunca passa pelo browser.

import { getSupabase } from '@infra/supabase/client';
import type {
  IPasswordResetRepository,
  PasswordResetLookup,
  PasswordResetStatus,
  AdminResetLink,
} from '@modules/auth/ports/IPasswordResetRepository';

const client = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase não configurado.');
  return s;
};

export class SupabasePasswordResetRepository implements IPasswordResetRepository {
  requestReset = async (email: string, origin: string): Promise<void> => {
    const { error } = await client().rpc('request_password_reset', {
      p_email: email,
      p_origin: origin,
    });
    if (error) throw new Error(error.message);
  };

  adminCreateReset = async (userId: string, origin: string): Promise<AdminResetLink> => {
    const { data, error } = await client().rpc('admin_create_password_reset', {
      p_user_id: userId,
      p_origin: origin,
    });
    if (error) throw new Error(error.message);
    const result = data as unknown as { link: string; email: string; expires_in_minutes: number };
    return {
      link: result.link,
      email: result.email,
      expiresInMinutes: result.expires_in_minutes,
    };
  };

  lookupReset = async (token: string): Promise<PasswordResetLookup> => {
    const { data, error } = await client().rpc('lookup_password_reset', { p_token: token });
    if (error) throw new Error(error.message);
    const result = (data ?? {}) as unknown as { valid?: boolean; status?: string; expires_at?: string | null };
    return {
      valid: !!result.valid,
      status: (result.status || 'invalid') as PasswordResetStatus,
      expires_at: result.expires_at ?? null,
    };
  };

  consumeReset = async (token: string, password: string): Promise<void> => {
    const { error } = await client().rpc('consume_password_reset', {
      p_token: token,
      p_password: password,
    });
    if (error) throw new Error(error.message);
  };
}
